import { useApolloClient } from "@apollo/client";
import {
  CustomFieldEntity,
  CustomFieldEntityState,
  CustomFieldType,
  ReportExpense,
  useCustomFieldsQuery,
} from "@common/utils/graphql";
import { useEffect, useMemo, useState } from "react";
import { useFormContext } from "react-hook-form";
import { z } from "zod";
import {
  getMultiLevelCustomFieldName,
  getParentFieldOptionUuid,
  isChildOfMultiLevelList,
  isListField,
} from "./utils";

const typeSchema = (value: CustomFieldType) =>
  z
    .enum([
      CustomFieldType.ShortText,
      CustomFieldType.List,
      CustomFieldType.Checkbox,
      CustomFieldType.MultiLevelList,
    ])
    .default(value);

const emptyCustomFields = [] as CustomFieldEntity[];
const emptyEditCustomFieldEntries = [] as ReportExpense["customFieldEntries"];
export const useCustomFieldFormConfig = (
  editCustomFieldEntries: ReportExpense["customFieldEntries"] = emptyEditCustomFieldEntries,
) => {
  const { data } = useCustomFieldsQuery({
    fetchPolicy: "cache-and-network",
  });

  const customFields =
    data?.visibleCustomFields.__typename === "QueryVisibleCustomFieldsSuccess"
      ? data.visibleCustomFields.data.customFields
      : emptyCustomFields;

  const fieldSchemas = customFields.reduce((acc, curr) => {
    let schema = null;
    if (isListField(curr)) {
      if (curr.state === CustomFieldEntityState.Required) {
        schema = {
          customFieldListOptionId: z.number({
            required_error: `You must select a ${curr.name}`,
            invalid_type_error: `You must select a ${curr.name}`,
          }),
        };
      } else {
        schema = {
          customFieldListOptionId: z.number().optional(),
        };
      }
    } else if (curr.type === CustomFieldType.Checkbox) {
      schema = {
        booleanValue: z.boolean().default(false),
      };
    } else if (curr.type === CustomFieldType.ShortText) {
      if (curr.state === CustomFieldEntityState.Required) {
        schema = {
          rawValue: z
            .string({ required_error: `You must enter a ${curr.name}` })
            .min(1, `You must enter a ${curr.name}`),
        };
      } else {
        schema = {
          rawValue: z.string().optional(),
        };
      }
    }
    return {
      ...acc,
      [curr.uuid]: z.object({
        type: typeSchema(curr.type),
        customFieldEntityId: z.number().default(curr.id),
        ...schema,
      }),
    };
  }, {});

  const customFieldSchema = {
    customFieldEntries: z.object(fieldSchemas),
  };

  const customFieldDefaultValues = useMemo(
    () =>
      customFields.reduce(
        (acc, curr) => {
          const editCustomFieldEntry = editCustomFieldEntries.find(
            (entry) => entry.customFieldEntityId === curr.id,
          );
          let defaultValue = null;
          if (isListField(curr)) {
            defaultValue = {
              customFieldListOptionId:
                editCustomFieldEntry?.customFieldListOptionId ?? undefined,
            };
          } else if (curr.type === CustomFieldType.Checkbox) {
            defaultValue = {
              booleanValue: editCustomFieldEntry?.booleanValue ?? false,
            };
          } else {
            defaultValue = {
              rawValue: editCustomFieldEntry?.rawValue ?? undefined,
            };
          }
          return {
            customFieldEntries: {
              ...acc.customFieldEntries,
              [curr.uuid]: {
                type: curr.type,
                customFieldEntityId: curr.id,
                ...defaultValue,
              },
            },
          };
        },
        { customFieldEntries: {} },
      ),
    [customFields, editCustomFieldEntries],
  );

  return { customFieldSchema, customFieldDefaultValues };
};

type useMultiLevelListParentFieldOptionUuidProps = {
  customField: CustomFieldEntity;
  onSelect: (id: number | null) => void;
};

// This hook is used to get the parent field option uuid for a child multi level list field
export const useMultiLevelListParentFieldOptionUuid = ({
  customField,
  onSelect,
}: useMultiLevelListParentFieldOptionUuidProps) => {
  const { getValues, watch } = useFormContext();
  const client = useApolloClient();
  const [parentFieldOptionUuid, setParentFieldOptionUuid] = useState<
    string | null
  >(() => {
    if (customField.parentEntityUuid) {
      const parentFieldOptionId = getValues(
        getMultiLevelCustomFieldName(customField.parentEntityUuid),
      );
      if (parentFieldOptionId) {
        return getParentFieldOptionUuid(client, parentFieldOptionId);
      }
    }
    return null;
  });

  // subscribe to form changes
  useEffect(() => {
    let unsubscribe = () => {};
    // only child multi level list fields should watch the parent field
    if (isChildOfMultiLevelList(customField)) {
      const subscription = watch((value: any, { name }: { name?: string }) => {
        // only update the parent field option uuid if the parent field changes
        if (
          name === getMultiLevelCustomFieldName(customField.parentEntityUuid!)
        ) {
          const parentFieldOptionId =
            value.customFieldEntries[customField.parentEntityUuid!]
              .customFieldListOptionId;
          setParentFieldOptionUuid(
            getParentFieldOptionUuid(client, parentFieldOptionId),
          );
          onSelect(null);
        }
      });
      unsubscribe = subscription.unsubscribe;
    }
    return () => unsubscribe();
  }, [watch, customField, onSelect, client]);

  return parentFieldOptionUuid;
};
